/** @jsx tsx */
import { tsx } from "@dojo/framework/core/vdom";
import { WidgetBase } from "@dojo/framework/core/WidgetBase";
import { cloneDeep, defaultsDeep } from "lodash";
import * as Constants from "../../Constants";
import { generateUUID, getShapeCenter, localizeString } from "../../store/reducers/util";
import { SelectionContainer, ShapeContainer } from "../containers/ShapeContainer";
import { CanvasMode } from "../interfaces/model/enumerations/CanvasMode";
import { GeometryTypes, IPageObjectTypes, AutosizeMode } from "../interfaces/model/enumerations/index";
import { IExtents } from "../interfaces/model/dataStructures";
import { ShapeType } from "../interfaces/model/enumerations/ShapeType";
import { ICustomGeometry } from "../interfaces/model/geometry/CustomGeometry";
import { IPresetGeometry } from "../interfaces/model/geometry/PresetGeometry";
import { IDrawShapeProperties } from "../interfaces/model/index";
import { IShape } from "../interfaces/model/PageObjects/Shape";
import { ILineProperties, ISolidFillProperties } from "../interfaces/model/properties/index";
import { cancelCurrentOperation, isExpandFromCenter, maintainAspectRatio } from "../keyUtil";
import { IDragResults, IPosition } from "../meta/Drag/CsiDragController";
import { CsiDrag } from "../meta/Drag/index";
import { getZoomValue } from "../selectors/index";
import { adjustPositionForRoot, getDefaultFroalaHtml, getShapeFitToTextContent } from "../util";
import { ICreateShapeRenderProps } from "./CanvasCreateShape";

/**
 * Determine whether or not a shape was drawn by a click or drag
 */
function _isClickShape(ext: IExtents): boolean {
	return ext.cx + ext.cy === 0;
}

/**
 * Determine whether shape exists and has at least a certain size
 */
function shapeExistsWithSize(shape: IShape, size: number): boolean {
	if (!shape) {
		return false;
	}
	const { ext } = shape.spPr.xfrm;
	return ext.cx >= size && ext.cy >= size; 
}

/**
 * Canvas Draw page.
 * Provides base component for drawing shapes on canvas. Here we use optimistic rendering,
 * a shape is rendered before adding to the state. This way user can see shape before it get's added,
 * as well it will get added in single operation.
 */
export class CreatePresetShape extends WidgetBase<ICreateShapeRenderProps> {
	/**
     * {IShape} Shape which is being drawn.
     */
	private shape: IShape | undefined;

	/**
     * Flag to indicate if dragging is possible or not
     */
	private _dragging = false;

	private _drawStartPosition: IPosition | undefined = undefined;

	/**
     * Holds event handlers registered for this widget. When widget is detached, these handlers will be removed.
     */
	private _boundHandlers: any[] = [];

	constructor() {
		super();
		this._boundHandlers = [];

		// Add event listeners for create preset shape
		[
			{ event: "pointerup", func: this._onPointerUp.bind(this) },
		].forEach((object) => {
			document.addEventListener(object.event, object.func);
			this._boundHandlers.push(object);
		});
	}

	/**
     * Pointer down event for shape
     * @param event {PointerEvent}
     */
	protected _onPointerDown(event: PointerEvent) {
		this._dragging = true;
		let shapeGeometry: IPresetGeometry | ICustomGeometry;
		const shapeDrawTools = this.properties.selection.shapeDrawTools as IDrawShapeProperties;

		// Check if draw tool set
		if (shapeDrawTools && shapeDrawTools.shapeType) {
			shapeGeometry = {
				presetShape: shapeDrawTools.shapeType,
				type: GeometryTypes.PRESET,
			};
		} else {
			// if undefined it is custom shape a curve will be drawn.
			shapeGeometry = {
				pathLst: [],
				type: GeometryTypes.CUSTOM,
			};
		}

		// Create basic structure for the shape.
		this.shape = {
			allowsArrows: shapeDrawTools.shapeType === ShapeType.line || !shapeDrawTools,
			allowsFill: typeof shapeDrawTools != undefined,
			noChangeAspect: false,
			shapeId: generateUUID(),
			spPr: {
				geometry: shapeGeometry,
				xfrm: {
					ext: { cx: 0, cy: 0 },
					flipH: false,
					flipV: false,
					off: {
						x: 0,
						y: 0,
					},
					rot: 0,
				},
			},
			type: IPageObjectTypes.SHAPE,
		};

		// Let's apply default shape properties for the shape.
		const defaults = shapeDrawTools && shapeDrawTools.isTextShape ?
			Constants.TEXT_SHAPE_DRAG_DEFAULT : Constants.DEFAULT_SHAPE_PROPERTIES;
		const userDefaults = shapeDrawTools && shapeDrawTools.isTextShape ?
			this.properties.selection.defaultTextShapeProperties : this.properties.selection.defaultShapeProperties;
		this.shape = defaultsDeep(this.shape, userDefaults, defaults);

		const { canvasContainerEl } = this.properties.api;
		const boundingBoxBound = canvasContainerEl.getBoundingClientRect();

		if (this.shape) {
			this._drawStartPosition = {
				x: getZoomValue(event.clientX - boundingBoxBound.left),
				y: getZoomValue(event.clientY - boundingBoxBound.top),
			};

			this.shape.spPr.xfrm.off.x = this._drawStartPosition.x;
			this.shape.spPr.xfrm.off.y = this._drawStartPosition.y;
		}
	}

	/**
     * Clean up on detach
     */
	protected onDetach(): void {
		this._boundHandlers.forEach((object) => document.removeEventListener(object.event, object.func));
		this.meta(CsiDrag)
			.remove("drawing-board");
	}

	/**
     * Pointer up event for shape
     * @param event {PointerEvent}
     */
	protected _onPointerUp() {
		if (this._dragging) {
			if (this.shape) {
				// destructuring
				const { spPr } = this.shape;
				// If there is no extents for shape and text mode is selected, creating default size text shape.
				const { ext, off } = spPr.xfrm;

				const shapeDrawTools = this.properties.selection.shapeDrawTools as IDrawShapeProperties;

				// If a placeholder shape is drawn out, draw as normal, if not, default to defaults.
				if (shapeDrawTools.isPlaceholder && _isClickShape(ext)) {
					ext.cx = Constants.DEFAULT_IMAGE_PLACEHOLDER_WIDTH;
					ext.cy = Constants.DEFAULT_IMAGE_PLACEHOLDER_HEIGHT;
				}

				const { api } = this.properties;
				// Apply default textShape properties to the textShape if there is no
				// user default's properties selected for shape.
				if (shapeDrawTools && shapeDrawTools.isTextShape && this.shape) {
					// If default text Shape properties are set then deleting the fill and ln from shape
					// and merging defaultTextShapeProperties with TEXT_SHAPE_DEFAULT.
					// else replacing fill and ln with TEXT_SHAPE_DEFAULT
					if (this.properties.selection.defaultTextShapeProperties) {
						delete spPr.fill;
						delete spPr.ln;
						const textShapeDefaults = this.properties.selection.defaultTextShapeProperties;
						this.shape = defaultsDeep(this.shape, textShapeDefaults, Constants.TEXT_SHAPE_DEFAULT);
					} else {
						spPr.fill = Constants.TEXT_SHAPE_DEFAULT.spPr.fill as ISolidFillProperties;
						spPr.ln = Constants.TEXT_SHAPE_DEFAULT.spPr.ln as ILineProperties;
					}

					let textboxHeight = Constants.TEXT_SHAPE_DEFAULT_HEIGHT;
					// try to get height of textbox based on default text styles
					const defaultTextStyles = this.properties.selection.defaultTextStyles;
					if (defaultTextStyles && this.shape) {
						const content = getDefaultFroalaHtml(defaultTextStyles).outerHTML;
						textboxHeight = getShapeFitToTextContent(this.shape, content).height;
						// If a shape is drawn by a click, set to auto width and center vertical align.
						if (_isClickShape(ext)) {
							spPr.autosize = defaultTextStyles[Constants.AUTOSIZE_KEY] as AutosizeMode ||
                            AutosizeMode.FIXED_HEIGHT;
							spPr.verticalAlign = Constants.TEXT_ALIGN.CENTER;
						} else {
							spPr.autosize = AutosizeMode.FIXED_SIZE;
							spPr.verticalAlign = defaultTextStyles[Constants.VERTICALALIGN_KEY] || Constants.TEXT_ALIGN.CENTER;
						}
					}

					ext.cx = Math.floor(ext.cx) || Constants.TEXT_SHAPE_DEFAULT_WIDTH;
					ext.cy = Math.floor(ext.cy) || textboxHeight + Constants.EXT_OFFSET;
				}

				if (shapeDrawTools && shapeDrawTools.isPlaceholder && this.shape) {
					const { spPr } = this.shape;
					spPr.fill = Constants.DEFAULT_PLACEHOLDER_SHAPE_PROPERTIES.spPr.fill as ISolidFillProperties;
					spPr.ln = Constants.DEFAULT_PLACEHOLDER_SHAPE_PROPERTIES.spPr.ln as ILineProperties;

					// Ensure dimensions are always drawn after drag
					this.shape.drawDimensions = true;

					const localizedPlaceholderText = localizeString(Constants.IMAGE_RESOURCE);

					this.shape.imagePlaceholder = true;
					this.shape.txBody = { content: "<p style=\"text-align: center;\"><span style=\"font-family: Arial;" +
                        ` font-size: 12px; color: rgb(0, 0, 0);\">${localizedPlaceholderText}</span></p>` };
				}

				// Add shape to actual pageObjects if it has valid height and width
				if (ext.cx >= Constants.SHAPE_MIN_SIZE && ext.cy >= Constants.SHAPE_MIN_SIZE) {
					// Reset canvas mode to select and add shape to the page.
					api.canvasApi.setCanvasMode(CanvasMode.SELECT);

					adjustPositionForRoot(api.canvasContainerEl, off);

					api.pageApi.addPageObject(this.shape);

					// If editOnComplete is true, then on drag complete shape should be in edit mode
					if (shapeDrawTools && shapeDrawTools.editOnComplete) {
						api.canvasApi.editPageObject();
					}
					// Reset shape draw tools on shape draw complete
					api.selectionApi.setShapeDrawTool();
				}
			}
			this.shape = undefined;
			this._dragging = false;
		}
	}

	/**
     * Pointer drag event for shape
     * @param drag {DragResults}
     */
	protected _onPointerDrag(drag: IDragResults) {
		const isEscapeKeyPress = cancelCurrentOperation(drag.keyData);
		if (isEscapeKeyPress) {
			this._dragging = false;
			this.shape = undefined;
			return;
		}
		if (drag.outOfEditor && !drag.isDragging) {
			this._onPointerUp();
			return;
		}
		if (!this._dragging || !drag.delta.x || !drag.delta.y) {
			return;
		}

		const shapeDrawTools = this.properties.selection.shapeDrawTools as IDrawShapeProperties;

		if (shapeDrawTools && shapeDrawTools.isPlaceholder && this.shape) {
			const placeholderProperties = this.shape.spPr.fill as ISolidFillProperties;
			placeholderProperties.color = Constants.IMAGE_PLACEHOLDER_FILL_COLOR;
		}

		this.shape = cloneDeep(this.shape);
		if (this.shape && this._drawStartPosition) {
			let refPoint = this._drawStartPosition;

			const bExpandFromCenter = isExpandFromCenter(drag.keyData);

			// In case of expand from center our reference point will be center of the shape.
			// We expand with respect distance between reference point and current pointer position.
			if (bExpandFromCenter) {
				refPoint = getShapeCenter(this.shape);
			}

			// check if drag is inverted as per start position
			if (drag.current && refPoint) {
				const invertDragX = refPoint.x > drag.current.x;
				const invertDragY = refPoint.y > drag.current.y;

				let width = Math.abs(refPoint.x - drag.current.x);
				let height = Math.abs(refPoint.y - drag.current.y);

				if (maintainAspectRatio(drag.keyData)) {
					width = height = Math.max(width, height);
				}

				if (invertDragX) {
					this.shape.spPr.xfrm.off.x = refPoint.x - width;
				} else if (bExpandFromCenter) {
					this.shape.spPr.xfrm.off.x = refPoint.x - width;
				} else {
					// setting drag start position as offset. In case user stopped expanding from center.
					this.shape.spPr.xfrm.off.x = this._drawStartPosition.x;
				}
				this.shape.spPr.xfrm.ext.cx = (bExpandFromCenter ? 2 : 1) * width;

				if (invertDragY) {
					this.shape.spPr.xfrm.off.y = refPoint.y - height;
				} else {
					if (bExpandFromCenter) {
						this.shape.spPr.xfrm.off.y = refPoint.y - height;
					} else {
						// setting drag start position as offset. In case user stopped expanding from center.
						this.shape.spPr.xfrm.off.y = this._drawStartPosition.y;
					}
				}

				this.shape.spPr.xfrm.ext.cy = (bExpandFromCenter ? 2 : 1) * height;
			}
		}
	}

	/**
	 * Override WidgetBase#render to produce a virtual DOM tree.
	 * @returns {HNode} Each time render() executes, it should build the entire virtual DOM tree.
	 */
	protected render() {
		const dragResult: IDragResults = this.meta(CsiDrag)
			.get("drawing-board");
		this._onPointerDrag(dragResult);

		if (this.properties.canvasEditor.mode === CanvasMode.DRAW) {
			if (shapeExistsWithSize(this.shape, Constants.SHAPE_MIN_SIZE)) {
				if (
					this.properties.selection.shapeDrawTools &&
                    this.properties.selection.shapeDrawTools.isPlaceholder &&
                    this.shape.spPr.xfrm.ext.cx >= (Constants.MINIMUM_IMAGE_PLACEHOLDER_WIDTH / 2)
				) {
					this.shape.drawDimensions = true;
				} else {
					this.shape.drawDimensions = false;
				}
			}
			return (
			// selection container should always show, while the page object itself should be behind the notes
			// position must be specified in order to use z-index
				<div
					classes="c8_canvas_modeDraw"
					key="drawing-board"
					onpointerdown={this._onPointerDown}
					styles={{ zIndex: `${Constants.MAX_Z_INDEX}` }}
				>
					{shapeExistsWithSize(this.shape, Constants.MIN_DIMENSION) && (
						<div>
							<SelectionContainer isTempShape pageObject={this.shape} />
							<ShapeContainer pageObject={this.shape} />
						</div>
					)}
				</div>
			);
		} else {
			return null;
		}
	}
}

export default CreatePresetShape;
